import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

/**
 * PortalPopover — rendert children in `document.body` op een fixed-position
 * berekend t.o.v. het anchor-element. Sluit bij klik buiten, scroll of resize.
 *
 * Lost het "dropdown clip in scroll-container" probleem op: omdat het paneel
 * via portal in <body> rendert, ontsnapt het aan alle overflow-hidden ouders.
 *
 * Props:
 *  - open: bool
 *  - onClose: () => void
 *  - anchorRef: React ref naar het element waar het paneel onder moet hangen
 *  - width: number (default 256)
 *  - align: "left" | "right" (default "left") — uitlijning aan anchor
 *  - className: extra classes voor het paneel
 */
export default function PortalPopover({
    open,
    onClose,
    anchorRef,
    width = 256,
    align = "left",
    className = "",
    children,
    testid,
}) {
    const popRef = useRef(null);
    const [pos, setPos] = useState({ top: 0, left: 0 });

    useEffect(() => {
        if (!open) return;
        const update = () => {
            const el = anchorRef?.current;
            if (!el) return;
            const r = el.getBoundingClientRect();
            let left = align === "right" ? r.right - width : r.left;
            // Houd binnen viewport
            if (left + width > window.innerWidth - 8) left = window.innerWidth - width - 8;
            if (left < 8) left = 8;
            setPos({ top: r.bottom + 4, left });
        };
        update();
        const onDown = (e) => {
            if (anchorRef?.current?.contains(e.target)) return;
            if (popRef.current?.contains(e.target)) return;
            onClose?.();
        };
        window.addEventListener("scroll", update, true);
        window.addEventListener("resize", update);
        document.addEventListener("mousedown", onDown);
        return () => {
            window.removeEventListener("scroll", update, true);
            window.removeEventListener("resize", update);
            document.removeEventListener("mousedown", onDown);
        };
    }, [open, anchorRef, align, width, onClose]);

    if (!open) return null;
    return createPortal(
        <div
            ref={popRef}
            data-testid={testid}
            style={{ position: "fixed", top: pos.top, left: pos.left, width, zIndex: 1000 }}
            className={`bg-white border border-border rounded-md shadow-lg max-h-72 overflow-auto ${className}`}
        >
            {children}
        </div>,
        document.body
    );
}
